/**
 * On-device cache for reference card data.
 *
 * Eras and per-set card lists change rarely; the set list now changes daily.
 * They were being refetched on every mount, and since the Cards route unmounts
 * whenever you navigate away, that meant a full round trip every time you came
 * back to a screen you had already looked at.
 *
 * Sizes are the reason this is worth doing and also the reason it is cheap:
 * eras are 2 KB, all 209 sets are 61 KB, and a set's card list is about 15 KB.
 * The whole browsable structure fits comfortably in localStorage.
 *
 * Reads are stale-while-revalidate. A cached value is returned immediately and
 * a refresh runs behind it, so a screen paints from disk and quietly gets
 * fresher rather than going blank and refilling.
 */

// 2: set and era logos moved to our own storage (2026-09-09).
// 4: a Japanese card name sat in the English catalogue (Evolutions 109 read
// "ナッシー[Exeggutor]"). The row was corrected, but a phone that had already
// cached it kept showing the old name, so the fix could not reach anybody
// without this moving.
// 5: the catalogue started carrying unreleased sets (2026-09-13). ME: 30th
// Celebration went in and did not appear on a phone that already held a set
// list, because that list was good for another week. Every cached copy has to
// go for the new sets to be seen at all.
// 6: printing counts were recounted and Ancient Mew's release date corrected
// (2026-09-17). Both live on the set list, which a phone holds for six hours,
// so the set tiles kept their old numbers and Ancient Mew kept sorting above
// Base Set. Same shape as 4 and 5: the data was fixed and the fix could not
// reach a device that already had an answer.
const VERSION = 6;
const PREFIX = `cc:cd:v${VERSION}:`;
const INDEX_KEY = `${PREFIX}index`;

// The set list used to be safe for a week, on the reasoning that a set appears
// roughly monthly and a few days late did not matter. That stopped being true
// the day the catalogue started carrying sets BEFORE they release: a presale set
// is at its most interesting in the days around its announcement, and a week is
// long enough for the whole moment to pass without anybody seeing it. Worse, a
// presale set GROWS - ME: 30th Celebration arrived with 26 cards and the vendor
// was already listing more - so a frozen card list goes wrong as well as stale.
//
// Reads are stale-while-revalidate, so a shorter window costs nothing on screen:
// the cached list still paints instantly and the refresh happens behind it.
const TTL_MS = {
  eras: 30 * 24 * 60 * 60 * 1000,
  sets: 6 * 60 * 60 * 1000,
  setCards: 24 * 60 * 60 * 1000,
};

// Per-set card lists accumulate as you browse. 60 sets is about 900 KB, which
// leaves plenty of headroom in a 5 MB store.
const MAX_SET_CARD_ENTRIES = 60;

// Same-session reads never touch storage. Parsing 61 KB of JSON on every render
// pass is its own kind of slow.
const memory = new Map();

function now() {
  return Date.now();
}

function safeGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    // Quota, private mode, or storage disabled. The cache is an optimisation,
    // never a dependency - callers still get live data.
    return false;
  }
}

function safeRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

/** Track which set-card entries exist and when they were last read, for eviction. */
function readIndex() {
  try {
    return JSON.parse(safeGet(INDEX_KEY) || '{}');
  } catch {
    return {};
  }
}

function touchIndex(key) {
  const idx = readIndex();
  idx[key] = now();

  const keys = Object.keys(idx);
  if (keys.length > MAX_SET_CARD_ENTRIES) {
    // Drop the least recently used until we are back under the cap.
    keys
      .sort((a, b) => idx[a] - idx[b])
      .slice(0, keys.length - MAX_SET_CARD_ENTRIES)
      .forEach((k) => {
        delete idx[k];
        memory.delete(k);
        safeRemove(k);
      });
  }

  safeSet(INDEX_KEY, JSON.stringify(idx));
}

/**
 * Read a cached value.
 * @returns {{data: any, stale: boolean}|null}
 */
export function readCache(key, kind) {
  const full = PREFIX + key;

  let entry = memory.get(full);
  if (!entry) {
    const raw = safeGet(full);
    if (!raw) return null;
    try {
      entry = JSON.parse(raw);
    } catch {
      safeRemove(full);
      return null;
    }
    memory.set(full, entry);
  }

  if (!entry || entry.data == null) return null;
  const ttl = TTL_MS[kind] ?? TTL_MS.sets;
  return { data: entry.data, stale: now() - (entry.at || 0) > ttl };
}

/** Write a value. Silently does nothing if storage is unavailable or full. */
export function writeCache(key, kind, data) {
  if (data == null) return;
  const full = PREFIX + key;
  const entry = { at: now(), data };
  memory.set(full, entry);
  const ok = safeSet(full, JSON.stringify(entry));
  if (ok && kind === 'setCards') touchIndex(full);
}

/**
 * Stale-while-revalidate around a fetcher.
 *
 * Returns cached data immediately when there is any, fresh or stale, and kicks
 * off a background refresh when it is stale. Only a complete miss waits on the
 * network.
 */
export async function cached(key, kind, fetcher) {
  const hit = readCache(key, kind);

  if (hit) {
    if (hit.stale) {
      // Not awaited. A stale value on screen beats a spinner.
      Promise.resolve()
        .then(fetcher)
        .then((fresh) => { if (fresh != null) writeCache(key, kind, fresh); })
        .catch(() => { /* offline or erroring: the stale value stands */ });
    }
    return hit.data;
  }

  const fresh = await fetcher();
  writeCache(key, kind, fresh);
  return fresh;
}

/** Wipe everything this module owns. Used when the data version moves. */
export function clearCardDataCache() {
  memory.clear();
  try {
    const doomed = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(PREFIX)) doomed.push(k);
    }
    doomed.forEach(safeRemove);
  } catch {
    /* nothing to clear */
  }
}

// Older cache versions are dead weight the moment VERSION moves.
(function dropStaleVersions() {
  try {
    const doomed = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith('cc:cd:v') && !k.startsWith(PREFIX)) doomed.push(k);
    }
    doomed.forEach(safeRemove);
  } catch {
    /* storage unavailable */
  }
})();

export default { readCache, writeCache, cached, clearCardDataCache };
